import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, supabase } from '../../lib/api';
import { compressImage, IMAGE_COMPRESS_PRESETS } from '../../lib/compressImage';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Input } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  canManagePmEvents,
  slugify,
  defaultEventPayload,
  getPmEventLiveUrl,
  getPmCustomDomainUrl,
  normalizeCustomDomain,
  isValidCustomDomainInput,
  buildPreviewMergeValues,
  applyPmEmailPreviewMerges,
  PM_STATUS_OPTIONS,
  PM_LANGUAGE_OPTIONS,
  PM_FORMAT_OPTIONS,
  PM_SPEAKER_LANDING_ROLES,
  PM_LANDING_TEMPLATES,
  resolveLandingTemplateId,
  PM_LANDING_SECTION_TYPES,
  landingSectionType,
  newLandingSection,
  newLandingSectionId,
  newLandingItem,
  prepareLandingSections,
  toPmPriceNumber,
  resolvePmEventPrice,
  formatPmPrice,
} from '../../lib/pmEventUtils';
import {
  PM_DEFAULT_TZ,
  pmTimezoneOptionsFor,
  isoToZonedInputValue,
  zonedInputValueToIso,
} from '../../lib/pmEventTime';
import SessionTimes from '../../components/Mentors/SessionTimes';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiTrash2, FiEdit2, FiUsers, FiUpload, FiChevronUp, FiChevronDown } = FiIcons;

const HOST_SOFT_LIMIT = 4;
const GUEST_SOFT_LIMIT = 4;
const FEATURED_SOFT_LIMIT = 6;
const FINPLAN_HOST_SOFT_LIMIT = 2;
const FINPLAN_SPEAKER_SOFT_LIMIT = 8;
const GUEST_BIO_MAX = 200;
const MD_ROLES = new Set(['manager', 'admin', 'super_admin']);

const EDITOR_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'payment', label: 'Payment' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'sections', label: 'Sections' },
  { id: 'partners', label: 'Partners' },
  { id: 'emails', label: 'Emails' },
];

const BASE_EMAIL_MERGE_FIELDS = [
  '{{first_name}}',
  '{{event_title}}',
  '{{session_date}}',
  '{{session_time}}',
  '{{session_location}}',
  '{{inviter_name}}',
  '{{inviter_title}}',
  '{{inviter_headshot_url}}',
  '{{inviter_email}}',
  '{{md_name}}',
  '{{md_title}}',
  '{{md_headshot_url}}',
];

/** Confirmation / reminder: {{session_time}} renders the multi-timezone block. */
const EMAIL_MERGE_FIELDS = [
  ...BASE_EMAIL_MERGE_FIELDS,
  '{{session_title}}',
  '{{session_meeting_url}}',
  '{{session_time_zones}}',
  '{{session_list}}',
  '{{session_time_primary}}',
  '{{session_timezones}}',
];

const EMAIL_MERGE_FIELDS_NOTE =
  '{{session_time}} shows the session time in its own timezone (large) plus Eastern / Central / Pacific underneath; in the subject it is the session timezone only. {{session_time_primary}} is the session timezone only; {{session_timezones}} is the other zones only.';

/**
 * {{paid_session_details}} is supported by the paid template only. The paid email is sent by
 * stripe-pm-webhook, which still renders {{session_time}} as a single-zone start time.
 */
const PAID_EMAIL_MERGE_FIELDS = [...BASE_EMAIL_MERGE_FIELDS, '{{paid_session_details}}'];

function defaultEmailSettings() {
  return {
    confirmation_enabled: true,
    confirmation_subject: '',
    confirmation_body: '',
    reminder_enabled: true,
    reminder_subject: '',
    reminder_body: '',
    reminder_offsets: [{ amount: 24, unit: 'hours' }],
    paid_confirmation_enabled: true,
    paid_confirmation_subject: '',
    paid_confirmation_body: '',
  };
}

function hoursBeforeToOffsetRow(hoursBefore) {
  const hours = Number(hoursBefore);
  if (Number.isFinite(hours) && hours >= 24 && hours % 24 === 0) {
    return { amount: hours / 24, unit: 'days' };
  }
  return { amount: Number.isFinite(hours) ? hours : 24, unit: 'hours' };
}

function offsetRowsToHoursBefore(rows) {
  return (rows || []).map((row) => {
    const amount = Math.max(0, Number(row.amount) || 0);
    const hours = row.unit === 'days' ? amount * 24 : amount;
    return { hours_before: hours };
  });
}

function emailFormFromRow(row) {
  if (!row) return defaultEmailSettings();
  const offsets = Array.isArray(row.reminder_offsets) && row.reminder_offsets.length
    ? row.reminder_offsets
    : [{ hours_before: 24 }];
  return {
    confirmation_enabled: row.confirmation_enabled !== false,
    confirmation_subject: row.confirmation_subject || '',
    confirmation_body: row.confirmation_body || '',
    reminder_enabled: row.reminder_enabled !== false,
    reminder_subject: row.reminder_subject || '',
    reminder_body: row.reminder_body || '',
    reminder_offsets: offsets.map((o) => hoursBeforeToOffsetRow(o?.hours_before)),
    paid_confirmation_enabled: row.paid_confirmation_enabled !== false,
    paid_confirmation_subject: row.paid_confirmation_subject || '',
    paid_confirmation_body: row.paid_confirmation_body || '',
  };
}

/** requiresPayment gates the paid template — a free event has no paid email to validate. */
function validateEmailSettings(emailForm, requiresPayment) {
  const errors = {};
  if (emailForm.confirmation_enabled) {
    if (!String(emailForm.confirmation_subject || '').trim()) {
      errors.confirmation_subject = 'Subject is required when confirmation email is enabled';
    }
    if (!String(emailForm.confirmation_body || '').trim()) {
      errors.confirmation_body = 'Body is required when confirmation email is enabled';
    }
  }
  if (emailForm.reminder_enabled) {
    if (!String(emailForm.reminder_subject || '').trim()) {
      errors.reminder_subject = 'Subject is required when reminder emails are enabled';
    }
    if (!String(emailForm.reminder_body || '').trim()) {
      errors.reminder_body = 'Body is required when reminder emails are enabled';
    }
    const offsets = offsetRowsToHoursBefore(emailForm.reminder_offsets);
    if (!offsets.length) {
      errors.reminder_offsets = 'Add at least one reminder offset when reminders are enabled';
    } else if (offsets.some((o) => !Number.isFinite(o.hours_before) || o.hours_before <= 0)) {
      errors.reminder_offsets = 'Each offset must be greater than zero';
    }
  }
  if (requiresPayment && emailForm.paid_confirmation_enabled) {
    if (!String(emailForm.paid_confirmation_subject || '').trim()) {
      errors.paid_confirmation_subject = 'Subject is required when the paid confirmation email is enabled';
    }
    if (!String(emailForm.paid_confirmation_body || '').trim()) {
      errors.paid_confirmation_body = 'Body is required when the paid confirmation email is enabled';
    }
  }
  return errors;
}

/** Mirrors the pm_events_payment_pricing_check / _early_bird_deadline_check constraints. */
function validatePaymentSettings(form) {
  const errors = {};
  const earlyBird = toPmPriceNumber(form.early_bird_price);
  const late = toPmPriceNumber(form.late_price);

  if (form.requires_payment && late === null && toPmPriceNumber(form.price) === null) {
    errors.late_price = 'A price is required when this event requires payment';
  }
  // Checked even when payment is off — the DB constraint is not conditional.
  if (earlyBird !== null && !String(form.early_bird_deadline || '').trim()) {
    errors.early_bird_deadline = 'Set when early-bird pricing ends, or clear the early-bird price';
  }
  if (form.early_bird_price !== '' && form.early_bird_price != null && earlyBird === null) {
    errors.early_bird_price = 'Enter a valid amount';
  }
  if (form.late_price !== '' && form.late_price != null && late === null) {
    errors.late_price = 'Enter a valid amount';
  }
  return errors;
}

function PmEmailHtmlPreview({ html, subject, title = 'Email Preview' }) {
  return (
    <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
      <div className="px-3 py-2 text-sm font-medium text-gray-900 border-b border-gray-200 bg-white">
        {subject || '(no subject)'}
      </div>
      <iframe
        srcDoc={html || '<p style="padding:20px;color:#999;font-family:sans-serif;">No HTML content yet.</p>'}
        sandbox="allow-same-origin"
        style={{ width: '100%', height: '480px', border: 'none' }}
        title={title}
      />
    </div>
  );
}

function EmailBodyEditor({
  mergeKey,
  value,
  onChange,
  disabled,
  error,
  textareaRef,
  placeholder,
  previewing,
  onTogglePreview,
  showInviterCard,
  onShowInviterCardChange,
  previewSubject,
  previewHtml,
  previewTitle,
  mergeFields = EMAIL_MERGE_FIELDS,
  note,
}) {
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className="font-medium text-gray-700 text-sm">Body</span>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={onTogglePreview}>
            {previewing ? 'Edit' : 'Preview'}
          </Button>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={showInviterCard}
              onChange={(e) => onShowInviterCardChange(e.target.checked)}
            />
            Show inviter card
          </label>
        </div>
      </div>
      {previewing ? (
        <>
          <PmEmailHtmlPreview html={previewHtml} subject={previewSubject} title={previewTitle} />
          <p className="mt-2 text-xs text-gray-400">
            Preview uses this event's title and first session, plus sample inviter data.
          </p>
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {mergeFields.map((token) => (
              <button
                key={`${mergeKey}-${token}`}
                type="button"
                disabled={disabled}
                onClick={() => insertTokenAtCursor(textareaRef.current, token, value, onChange)}
                className="px-2 py-0.5 text-xs rounded border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900 disabled:opacity-40"
              >
                {token}
              </button>
            ))}
          </div>
          {note && <p className="text-xs text-gray-500 mb-2">{note}</p>}
          <textarea
            ref={textareaRef}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[180px] font-sans placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
          />
        </>
      )}
      {error && (
        <p className="text-sm text-red-500 mt-1" role="alert">{error}</p>
      )}
    </div>
  );
}

function insertTokenAtCursor(textarea, token, value, onChange) {
  if (!textarea) {
    onChange(`${value || ''}${token}`);
    return;
  }
  const start = textarea.selectionStart ?? (value || '').length;
  const end = textarea.selectionEnd ?? start;
  const next = `${(value || '').slice(0, start)}${token}${(value || '').slice(end)}`;
  onChange(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const pos = start + token.length;
    textarea.setSelectionRange(pos, pos);
  });
}

const HERO_BUCKET = 'pm-event-heroes';
const HERO_MAX_BYTES = 5 * 1024 * 1024;
const HERO_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

function heroExtFromFile(file) {
  const fromName = String(file?.name || '').split('.').pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{1,5}$/.test(fromName)) return fromName;
  const mime = String(file?.type || '').toLowerCase();
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  return 'jpg';
}

function speakerPreview(sp) {
  return {
    name: sp.name ?? resolveDisplayName(sp.user_id, sp.user) ?? 'Speaker',
    title: sp.title ?? sp.user?.title ?? null,
    photo_url: sp.photo_url ?? sp.user?.headshot_url ?? null,
  };
}

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}

function fromLocalInputValue(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function moveListItem(list, index, delta) {
  const next = [...list];
  const target = index + delta;
  if (target < 0 || target >= next.length) return list;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

function emptySessionDraft() {
  return {
    title: '',
    starts_at: '',
    ends_at: '',
    timezone: PM_DEFAULT_TZ,
    location_text: '',
    meeting_url: '',
    capacity: '',
    requires_payment: false,
  };
}

function SessionTimezoneField({ value, onChange }) {
  return (
    <label className="block text-sm sm:col-span-2">
      <span className="font-medium text-gray-700 mb-1 block">Timezone</span>
      <select
        className="w-full sm:w-1/2 border border-gray-300 rounded-md h-10 px-3 text-sm bg-white"
        value={value || PM_DEFAULT_TZ}
        onChange={(e) => onChange(e.target.value)}
      >
        {pmTimezoneOptionsFor(value).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="text-xs text-gray-500 mt-1 block">Times are entered in the selected timezone.</span>
    </label>
  );
}

function sessionDraftFromRow(session) {
  const timezone = session.timezone?.trim() || PM_DEFAULT_TZ;
  return {
    title: session.title || '',
    starts_at: isoToZonedInputValue(session.starts_at, timezone),
    ends_at: isoToZonedInputValue(session.ends_at, timezone),
    timezone,
    location_text: session.location_text || '',
    meeting_url: session.meeting_url || '',
    capacity: session.capacity != null ? String(session.capacity) : '',
    requires_payment: session.requires_payment === true,
  };
}

export default function EventEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const canManage = canManagePmEvents(user);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState(defaultEventPayload());
  const [assigneeManual, setAssigneeManual] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [partners, setPartners] = useState([]);
  const { users: directoryUsers } = useUsersDirectory();
  const agencyUsers = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const mdAllowlist = useMemo(
    () => agencyUsers.filter((u) => MD_ROLES.has(u.role)),
    [agencyUsers],
  );

  const [sessionDraft, setSessionDraft] = useState(emptySessionDraft);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionEditDraft, setSessionEditDraft] = useState(emptySessionDraft);
  const [hostDraft, setHostDraft] = useState({
    mode: 'user',
    user_id: '',
    name: '',
    title: '',
    photo_url: '',
  });
  const [guestDraft, setGuestDraft] = useState({
    name: '',
    title: '',
    bio: '',
    partner_id: '',
    photo_url: '',
  });
  const [featuredDraft, setFeaturedDraft] = useState({
    mode: 'user',
    user_id: '',
    name: '',
    title: '',
    photo_url: '',
  });
  const [partnerDraft, setPartnerDraft] = useState({
    name: '',
    logo_url: '',
  });

  const [mcBusyId, setMcBusyId] = useState(null);
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const [sectionPhotoKey, setSectionPhotoKey] = useState('');

  const [heroUploading, setHeroUploading] = useState(false);
  const [heroUploadError, setHeroUploadError] = useState('');
  const [localHeroPreview, setLocalHeroPreview] = useState(null);
  const heroFileInputRef = useRef(null);

  const [partnerLogoUploading, setPartnerLogoUploading] = useState(false);
  const [partnerLogoUploadError, setPartnerLogoUploadError] = useState('');
  const [localPartnerLogoPreview, setLocalPartnerLogoPreview] = useState(null);
  const partnerLogoFileInputRef = useRef(null);

  const [speakerPhotoUploading, setSpeakerPhotoUploading] = useState(false);
  const [speakerPhotoUploadError, setSpeakerPhotoUploadError] = useState('');
  const [localSpeakerPhotoPreview, setLocalSpeakerPhotoPreview] = useState(null);
  const speakerPhotoFileInputRef = useRef(null);

  const [hostPhotoUploading, setHostPhotoUploading] = useState(false);
  const [hostPhotoUploadError, setHostPhotoUploadError] = useState('');
  const [localHostPhotoPreview, setLocalHostPhotoPreview] = useState(null);
  const hostPhotoFileInputRef = useRef(null);

  const [featuredPhotoUploading, setFeaturedPhotoUploading] = useState(false);
  const [featuredPhotoUploadError, setFeaturedPhotoUploadError] = useState('');
  const [localFeaturedPhotoPreview, setLocalFeaturedPhotoPreview] = useState(null);
  const featuredPhotoFileInputRef = useRef(null);

  const [emailForm, setEmailForm] = useState(defaultEmailSettings);
  const [emailSettingsId, setEmailSettingsId] = useState(null);
  const [emailSettingsLoaded, setEmailSettingsLoaded] = useState(false);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [emailSettingsDirty, setEmailSettingsDirty] = useState(false);
  const [emailErrors, setEmailErrors] = useState({});
  const [paymentErrors, setPaymentErrors] = useState({});
  const [confirmationPreviewing, setConfirmationPreviewing] = useState(false);
  const [reminderPreviewing, setReminderPreviewing] = useState(false);
  const [paidPreviewing, setPaidPreviewing] = useState(false);
  const [confirmationShowInviter, setConfirmationShowInviter] = useState(true);
  const [reminderShowInviter, setReminderShowInviter] = useState(true);
  const [paidShowInviter, setPaidShowInviter] = useState(true);
  const emailSettingsLoadedForId = useRef(null);
  const confirmationBodyRef = useRef(null);
  const reminderBodyRef = useRef(null);
  const paidBodyRef = useRef(null);

  const loadEvent = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const data = await api.pmEvents.getById(id);
      if (!data) {
        addToast({ type: 'error', title: 'Event not found' });
        navigate('/mentors');
        return;
      }
      setForm({
        title: data.title,
        short_description: data.short_description || '',
        slug: data.slug,
        custom_domain: data.custom_domain || '',
        language: data.language,
        format: data.format,
        requires_payment: data.requires_payment,
        price: data.price,
        early_bird_price: data.early_bird_price ?? '',
        early_bird_deadline: toLocalInputValue(data.early_bird_deadline),
        late_price: data.late_price ?? '',
        is_main_event: data.is_main_event,
        landing_template_id: data.landing_template_id,
        landing_headline: data.landing_headline || '',
        landing_badge: data.landing_badge || '',
        registration_scope: data.registration_scope === 'all_sessions' ? 'all_sessions' : 'single_session',
        landing_sections: (Array.isArray(data.landing_sections) ? data.landing_sections : []).map((section) => ({
          ...section,
          id: section?.id || newLandingSectionId(),
        })),
        theme: data.theme,
        density: data.density,
        hero_style: data.hero_style,
        hero_image_url: data.hero_image_url || '',
        allow_self_serve_guests: data.allow_self_serve_guests,
        status: data.status,
        agent_id: data.agent_id || null,
        marketing_director_id: data.marketing_director_id || null,
        pipeline_event_type: data.pipeline_event_type || null,
        business_launch_assignee_name: data.business_launch_assignee_name || '',
        business_launch_assignee_phone: data.business_launch_assignee_phone || '',
        business_launch_assignee_email: data.business_launch_assignee_email || '',
      });
      setAssigneeManual(
        !data.agent_id
        && Boolean(
          data.business_launch_assignee_name
          || data.business_launch_assignee_phone
          || data.business_launch_assignee_email,
        ),
      );
      setSessions(data.sessions ?? []);
      setSpeakers(data.speakers ?? []);
      setPartners(data.partners ?? []);
      setSlugTouched(true);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load event', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id, isNew, addToast, navigate]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    setEmailForm(defaultEmailSettings());
    setEmailSettingsId(null);
    setEmailSettingsLoaded(false);
    setEmailSettingsDirty(false);
    setEmailErrors({});
    emailSettingsLoadedForId.current = null;
  }, [id]);

  useEffect(() => {
    if (isNew || activeTab !== 'emails' || !id) return;
    if (emailSettingsLoaded && emailSettingsLoadedForId.current === id) return;

    let cancelled = false;
    (async () => {
      setEmailSettingsLoading(true);
      try {
        const row = await api.pmEventEmailSettings.getByEvent(id);
        if (cancelled) return;
        setEmailForm(emailFormFromRow(row));
        setEmailSettingsId(row?.id ?? null);
        setEmailSettingsDirty(false);
        setEmailErrors({});
        setEmailSettingsLoaded(true);
        emailSettingsLoadedForId.current = id;
      } catch (err) {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Failed to load email settings', message: err.message });
        }
      } finally {
        if (!cancelled) setEmailSettingsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, id, isNew, emailSettingsLoaded, addToast]);

  useEffect(() => {
    if (!slugTouched && form.title) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, slugTouched]);

  useEffect(() => () => {
    if (localHeroPreview) URL.revokeObjectURL(localHeroPreview);
  }, [localHeroPreview]);

  useEffect(() => () => {
    if (localPartnerLogoPreview) URL.revokeObjectURL(localPartnerLogoPreview);
  }, [localPartnerLogoPreview]);

  useEffect(() => () => {
    if (localSpeakerPhotoPreview) URL.revokeObjectURL(localSpeakerPhotoPreview);
  }, [localSpeakerPhotoPreview]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setPaymentField = (key, value) => {
    setField(key, value);
    setPaymentErrors((errs) => {
      const next = { ...errs };
      delete next[key];
      if (key === 'early_bird_price' && String(value).trim() === '') delete next.early_bird_deadline;
      return next;
    });
  };

  // Same resolution the checkout function runs, so this is what a registrant pays today.
  const currentPrice = useMemo(
    () => resolvePmEventPrice({
      early_bird_price: form.early_bird_price,
      early_bird_deadline: fromLocalInputValue(form.early_bird_deadline),
      late_price: form.late_price,
      price: form.price,
    }),
    [form.early_bird_price, form.early_bird_deadline, form.late_price, form.price],
  );
  const hasEarlyBird = toPmPriceNumber(form.early_bird_price) !== null;

  const setEmailField = (key, value) => {
    setEmailForm((f) => ({ ...f, [key]: value }));
    setEmailSettingsDirty(true);
    setEmailErrors((errs) => {
      const next = { ...errs };
      delete next[key];
      if (key === 'confirmation_enabled' && !value) {
        delete next.confirmation_subject;
        delete next.confirmation_body;
      }
      if (key === 'reminder_enabled' && !value) {
        delete next.reminder_subject;
        delete next.reminder_body;
        delete next.reminder_offsets;
      }
      if (key === 'paid_confirmation_enabled' && !value) {
        delete next.paid_confirmation_subject;
        delete next.paid_confirmation_body;
      }
      return next;
    });
  };

  const previewMergeValues = useMemo(
    () => buildPreviewMergeValues(form, sessions),
    [form, sessions],
  );
  const confirmationPreviewSubject = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.confirmation_subject, previewMergeValues),
    [emailForm.confirmation_subject, previewMergeValues],
  );
  const confirmationPreviewHtml = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.confirmation_body, previewMergeValues, {
      showInviterCard: confirmationShowInviter,
      html: true,
    }),
    [emailForm.confirmation_body, previewMergeValues, confirmationShowInviter],
  );
  const reminderPreviewSubject = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.reminder_subject, previewMergeValues),
    [emailForm.reminder_subject, previewMergeValues],
  );
  const reminderPreviewHtml = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.reminder_body, previewMergeValues, {
      showInviterCard: reminderShowInviter,
      html: true,
    }),
    [emailForm.reminder_body, previewMergeValues, reminderShowInviter],
  );
  const paidPreviewMergeValues = useMemo(
    () => buildPreviewMergeValues(form, sessions, { multiZone: false }),
    [form, sessions],
  );
  const paidPreviewSubject = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.paid_confirmation_subject, paidPreviewMergeValues),
    [emailForm.paid_confirmation_subject, paidPreviewMergeValues],
  );
  const paidPreviewHtml = useMemo(
    () => applyPmEmailPreviewMerges(emailForm.paid_confirmation_body, paidPreviewMergeValues, {
      showInviterCard: paidShowInviter,
      html: true,
    }),
    [emailForm.paid_confirmation_body, paidPreviewMergeValues, paidShowInviter],
  );

  const uploadToHeroBucket = async (file, filenamePrefix, presetKey = filenamePrefix) => {
    if (!user?.id) throw new Error('You must be signed in to upload.');
    if (!supabase) throw new Error('Storage is unavailable in this environment.');
    if (!String(file.type || '').startsWith('image/')) {
      throw new Error('Please choose an image file (JPEG, PNG, WebP, or GIF).');
    }
    if (file.size > HERO_MAX_BYTES) {
      throw new Error('Image must be 5 MB or smaller.');
    }
    const preset = IMAGE_COMPRESS_PRESETS[presetKey];
    if (!preset) throw new Error(`No image preset for ${presetKey}`);
    const uploadFile = await compressImage(file, preset);
    const ext = heroExtFromFile(uploadFile);
    const folder = isNew ? 'draft' : id;
    const path = `${user.id}/${folder}/${filenamePrefix}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from(HERO_BUCKET)
      .upload(path, uploadFile, {
        cacheControl: '31536000',
        upsert: false,
        contentType: uploadFile.type || `image/${ext}`,
      });
    if (uploadError) throw uploadError;

    const { data: publicData } = supabase.storage.from(HERO_BUCKET).getPublicUrl(path);
    const publicUrl = publicData?.publicUrl;
    if (!publicUrl) throw new Error('Upload succeeded but no public URL was returned.');
    return publicUrl;
  };

  const landingSections = Array.isArray(form.landing_sections) ? form.landing_sections : [];

  const patchLandingSection = (sectionId, updater) => {
    setForm((current) => ({
      ...current,
      landing_sections: (Array.isArray(current.landing_sections) ? current.landing_sections : []).map((section) => (
        section.id === sectionId ? updater(section) : section
      )),
    }));
  };

  const addLandingSection = (typeKey) => {
    if (!canManage) return;
    if (landingSections.some((section) => section.type === typeKey)) return;
    const created = newLandingSection(typeKey);
    if (!created) return;
    setForm((current) => ({
      ...current,
      landing_sections: [...(Array.isArray(current.landing_sections) ? current.landing_sections : []), created],
    }));
    setSectionMenuOpen(false);
  };

  const uploadSectionItemPhoto = async (sectionId, itemIndex, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canManage) return;
    const key = `${sectionId}:${itemIndex}`;
    setSectionPhotoKey(key);
    try {
      const publicUrl = await uploadToHeroBucket(file, 'testimonial', 'speaker-photo');
      patchLandingSection(sectionId, (section) => {
        const items = [...(section.items || [])];
        items[itemIndex] = { ...items[itemIndex], photo_url: publicUrl };
        return { ...section, items };
      });
      addToast({ type: 'success', title: 'Testimonial photo uploaded' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not upload photo', message: err.message });
    } finally {
      setSectionPhotoKey('');
    }
  };

  const handleHeroUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setHeroUploadError('');
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setLocalHeroPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setHeroUploading(true);
    try {
      const publicUrl = await uploadToHeroBucket(file, 'hero');
      setField('hero_image_url', publicUrl);
      setLocalHeroPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      addToast({
        type: 'success',
        title: 'Hero image uploaded',
        message: isNew ? 'URL set — save the event to keep it.' : 'URL updated — save the event to keep it.',
      });
    } catch (err) {
      console.error('[EventEditor] hero upload failed:', err);
      setHeroUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setHeroUploading(false);
    }
  };

  const handlePartnerLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setPartnerLogoUploadError('');
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setLocalPartnerLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setPartnerLogoUploading(true);
    try {
      const publicUrl = await uploadToHeroBucket(file, 'partner-logo');
      setPartnerDraft((d) => ({ ...d, logo_url: publicUrl }));
      setLocalPartnerLogoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      addToast({ type: 'success', title: 'Partner logo uploaded' });
    } catch (err) {
      console.error('[EventEditor] partner logo upload failed:', err);
      setPartnerLogoUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setPartnerLogoUploading(false);
    }
  };

  const uploadSpeakerHeadshot = async (event, {
    setUploading,
    setUploadError,
    setLocalPreview,
    applyUrl,
  }) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setUploadError('');
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });

    setUploading(true);
    try {
      const publicUrl = await uploadToHeroBucket(file, 'speaker-photo');
      applyUrl(publicUrl);
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      addToast({ type: 'success', title: 'Speaker photo uploaded' });
    } catch (err) {
      console.error('[EventEditor] speaker photo upload failed:', err);
      setUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSpeakerPhotoUpload = (event) => uploadSpeakerHeadshot(event, {
    setUploading: setSpeakerPhotoUploading,
    setUploadError: setSpeakerPhotoUploadError,
    setLocalPreview: setLocalSpeakerPhotoPreview,
    applyUrl: (publicUrl) => setGuestDraft((d) => ({ ...d, photo_url: publicUrl })),
  });

  const handleHostPhotoUpload = (event) => uploadSpeakerHeadshot(event, {
    setUploading: setHostPhotoUploading,
    setUploadError: setHostPhotoUploadError,
    setLocalPreview: setLocalHostPhotoPreview,
    applyUrl: (publicUrl) => setHostDraft((d) => ({ ...d, photo_url: publicUrl })),
  });

  const handleFeaturedPhotoUpload = (event) => uploadSpeakerHeadshot(event, {
    setUploading: setFeaturedPhotoUploading,
    setUploadError: setFeaturedPhotoUploadError,
    setLocalPreview: setLocalFeaturedPhotoPreview,
    applyUrl: (publicUrl) => setFeaturedDraft((d) => ({ ...d, photo_url: publicUrl })),
  });

  const clearLocalPhotoPreview = (setLocalPreview) => {
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!canManage) {
      addToast({ type: 'error', title: 'You do not have permission to manage events' });
      return;
    }
    if (!form.title?.trim() || !form.slug?.trim()) {
      addToast({ type: 'error', title: 'Title and slug are required' });
      return;
    }
    if (!isValidCustomDomainInput(form.custom_domain)) {
      addToast({
        type: 'error',
        title: 'Invalid custom domain',
        message: 'Use a bare hostname like fintechexpansion.com (no https:// or path).',
      });
      return;
    }

    if (form.pipeline_event_type === 'business_launch') {
      const hasAgent = Boolean(form.agent_id);
      const hasManualName = Boolean(String(form.business_launch_assignee_name || '').trim());
      if (!hasAgent && !hasManualName) {
        addToast({
          type: 'error',
          title: 'Assignee required',
          message: 'Pick a CRM user or enter a name for the person not in the system yet.',
        });
        return;
      }
    }

    const payErrs = validatePaymentSettings(form);
    if (Object.keys(payErrs).length) {
      setPaymentErrors(payErrs);
      setActiveTab('payment');
      addToast({ type: 'error', title: 'Fix payment settings before saving' });
      return;
    }
    setPaymentErrors({});

    const preparedSections = prepareLandingSections(form.landing_sections);
    if (preparedSections.error) {
      setActiveTab('sections');
      addToast({ type: 'error', title: 'Fix landing sections', message: preparedSections.error });
      return;
    }
    const landingHeadline = String(form.landing_headline || '').trim();
    const landingBadge = String(form.landing_badge || '').trim();
    if (landingHeadline.length > 120 || landingBadge.length > 30) {
      setActiveTab('details');
      addToast({ type: 'error', title: 'Headline or badge is too long' });
      return;
    }

    if (!isNew && emailSettingsDirty) {
      const errs = validateEmailSettings(emailForm, Boolean(form.requires_payment));
      if (Object.keys(errs).length) {
        setEmailErrors(errs);
        setActiveTab('emails');
        addToast({ type: 'error', title: 'Fix email settings before saving' });
        return;
      }
    }

    setSaving(true);
    try {
      const isBusinessLaunch = form.pipeline_event_type === 'business_launch';
      const assignedAgentId = isBusinessLaunch
        ? (form.agent_id || null)
        : (form.agent_id || user.id);
      const useManualAssignee = isBusinessLaunch && !assignedAgentId;
      const payload = {
        ...form,
        landing_template_id: resolveLandingTemplateId(form.landing_template_id),
        landing_headline: landingHeadline || null,
        landing_badge: landingBadge || null,
        registration_scope: form.registration_scope === 'all_sessions' ? 'all_sessions' : 'single_session',
        landing_sections: preparedSections.sections,
        hero_image_url: form.hero_image_url?.trim() || null,
        short_description: form.short_description?.trim() || null,
        custom_domain: normalizeCustomDomain(form.custom_domain),
        early_bird_price: toPmPriceNumber(form.early_bird_price),
        early_bird_deadline: fromLocalInputValue(form.early_bird_deadline),
        late_price: toPmPriceNumber(form.late_price),
        pipeline_event_type: form.pipeline_event_type || null,
        agent_id: assignedAgentId,
        marketing_director_id: form.marketing_director_id || null,
        business_launch_assignee_name: useManualAssignee
          ? (form.business_launch_assignee_name?.trim() || null)
          : null,
        business_launch_assignee_phone: useManualAssignee
          ? (form.business_launch_assignee_phone?.trim() || null)
          : null,
        business_launch_assignee_email: useManualAssignee
          ? (form.business_launch_assignee_email?.trim() || null)
          : null,
      };
      if (isNew) {
        const exists = await api.pmEvents.slugExists(user.agency_id, form.slug);
        if (exists) throw new Error('This slug is already used in your agency');
        const created = await api.pmEvents.create({
          ...payload,
          agency_id: user.agency_id,
        });
        addToast({ type: 'success', title: 'Event created' });
        navigate(`/mentors/${created.id}`, { replace: true });
      } else {
        const exists = await api.pmEvents.slugExists(user.agency_id, form.slug, id);
        if (exists) throw new Error('This slug is already used in your agency');
        await api.pmEvents.update(id, payload);
        setForm((current) => ({
          ...current,
          landing_headline: landingHeadline,
          landing_badge: landingBadge,
          registration_scope: payload.registration_scope,
          landing_sections: preparedSections.sections,
        }));

        if (emailSettingsDirty) {
          const saved = await api.pmEventEmailSettings.upsert({
            event_id: id,
            agency_id: user.agency_id,
            confirmation_enabled: Boolean(emailForm.confirmation_enabled),
            confirmation_subject: String(emailForm.confirmation_subject || '').trim() || null,
            confirmation_body: String(emailForm.confirmation_body || '').trim() || null,
            reminder_enabled: Boolean(emailForm.reminder_enabled),
            reminder_subject: String(emailForm.reminder_subject || '').trim() || null,
            reminder_body: String(emailForm.reminder_body || '').trim() || null,
            reminder_offsets: offsetRowsToHoursBefore(emailForm.reminder_offsets),
            // Free events never render the paid card; omitting the fields leaves the
            // seeded paid template in place rather than blanking it.
            ...(form.requires_payment
              ? {
                  paid_confirmation_enabled: Boolean(emailForm.paid_confirmation_enabled),
                  paid_confirmation_subject: String(emailForm.paid_confirmation_subject || '').trim() || null,
                  paid_confirmation_body: String(emailForm.paid_confirmation_body || '').trim() || null,
                }
              : {}),
          });
          setEmailSettingsId(saved?.id ?? emailSettingsId);
          setEmailSettingsDirty(false);
          setEmailErrors({});
        }

        addToast({ type: 'success', title: 'Event saved' });
        await loadEvent();
      }
    } catch (err) {
      addToast({ type: 'error', title: 'Save failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const addSession = async () => {
    if (!sessionDraft.starts_at) {
      addToast({ type: 'error', title: 'Session start time is required' });
      return;
    }
    try {
      const row = await api.pmEventSessions.create({
        event_id: id,
        agency_id: user.agency_id,
        title: sessionDraft.title || null,
        starts_at: zonedInputValueToIso(sessionDraft.starts_at, sessionDraft.timezone),
        ends_at: zonedInputValueToIso(sessionDraft.ends_at, sessionDraft.timezone),
        timezone: sessionDraft.timezone || PM_DEFAULT_TZ,
        location_text: sessionDraft.location_text || null,
        meeting_url: sessionDraft.meeting_url || null,
        capacity: sessionDraft.capacity ? Number(sessionDraft.capacity) : null,
        // Free events cannot gate sessions, so never persist a stale checkbox value.
        requires_payment: Boolean(form.requires_payment && sessionDraft.requires_payment),
        sort_order: sessions.length,
      });
      setSessions((s) => [...s, row]);
      setSessionDraft(emptySessionDraft());
      addToast({ type: 'success', title: 'Session added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add session', message: err.message });
    }
  };

  const removeSession = async (sessionId) => {
    try {
      await api.pmEventSessions.delete(sessionId);
      setSessions((s) => s.filter((x) => x.id !== sessionId));
      if (editingSessionId === sessionId) {
        setEditingSessionId(null);
        setSessionEditDraft(emptySessionDraft());
      }
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete session', message: err.message });
    }
  };

  const startEditSession = (session) => {
    setEditingSessionId(session.id);
    setSessionEditDraft(sessionDraftFromRow(session));
  };

  const cancelEditSession = () => {
    setEditingSessionId(null);
    setSessionEditDraft(emptySessionDraft());
  };

  const saveSessionEdit = async () => {
    if (!editingSessionId) return;
    if (!sessionEditDraft.starts_at) {
      addToast({ type: 'error', title: 'Session start time is required' });
      return;
    }
    try {
      const row = await api.pmEventSessions.update(editingSessionId, {
        title: sessionEditDraft.title || null,
        starts_at: zonedInputValueToIso(sessionEditDraft.starts_at, sessionEditDraft.timezone),
        ends_at: zonedInputValueToIso(sessionEditDraft.ends_at, sessionEditDraft.timezone),
        timezone: sessionEditDraft.timezone || PM_DEFAULT_TZ,
        location_text: sessionEditDraft.location_text || null,
        meeting_url: sessionEditDraft.meeting_url || null,
        capacity: sessionEditDraft.capacity ? Number(sessionEditDraft.capacity) : null,
        requires_payment: Boolean(form.requires_payment && sessionEditDraft.requires_payment),
      });
      setSessions((s) => s.map((x) => (x.id === editingSessionId ? row : x)));
      setEditingSessionId(null);
      setSessionEditDraft(emptySessionDraft());
      addToast({ type: 'success', title: 'Session updated' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not update session', message: err.message });
    }
  };

  const addHost = async ({ isMc = false } = {}) => {
    try {
      const payload = {
        event_id: id,
        agency_id: user.agency_id,
        role: 'speaker',
        is_mc: isMc === true,
        sort_order: speakers.length,
      };
      if (hostDraft.mode === 'user') {
        if (!hostDraft.user_id) throw new Error('Select a host');
        payload.user_id = hostDraft.user_id;
      } else {
        if (!hostDraft.name.trim()) throw new Error('Host name is required');
        payload.name = hostDraft.name.trim();
        payload.title = hostDraft.title.trim() || null;
        payload.photo_url = hostDraft.photo_url.trim() || null;
      }
      const row = await api.pmEventSpeakers.create(payload);
      setSpeakers((s) => [...s, row]);
      setHostDraft({ mode: 'user', user_id: '', name: '', title: '', photo_url: '' });
      setHostPhotoUploadError('');
      clearLocalPhotoPreview(setLocalHostPhotoPreview);
      addToast({ type: 'success', title: isMc ? 'MC / host added' : 'Host added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add host', message: err.message });
    }
  };

  const addGuestSpeaker = async () => {
    try {
      if (!guestDraft.name.trim()) throw new Error('Guest name is required');
      const payload = {
        event_id: id,
        agency_id: user.agency_id,
        role: 'special_guest',
        is_mc: false,
        name: guestDraft.name.trim(),
        title: guestDraft.title.trim() || null,
        bio: guestDraft.bio.trim() || null,
        photo_url: guestDraft.photo_url.trim() || null,
        partner_id: guestDraft.partner_id || null,
        sort_order: speakers.length,
      };
      const row = await api.pmEventSpeakers.create(payload);
      setSpeakers((s) => [...s, row]);
      setGuestDraft({ name: '', title: '', bio: '', partner_id: '', photo_url: '' });
      setSpeakerPhotoUploadError('');
      clearLocalPhotoPreview(setLocalSpeakerPhotoPreview);
      addToast({
        type: 'success',
        title: resolveLandingTemplateId(form.landing_template_id) === 'finplan' ? 'Speaker added' : 'Guest speaker added',
      });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add guest speaker', message: err.message });
    }
  };

  const addFeaturedLeader = async () => {
    try {
      const payload = {
        event_id: id,
        agency_id: user.agency_id,
        role: 'featured_leader',
        is_mc: false,
        sort_order: speakers.length,
      };
      if (featuredDraft.mode === 'user') {
        if (!featuredDraft.user_id) throw new Error('Select a featured leader');
        payload.user_id = featuredDraft.user_id;
      } else {
        if (!featuredDraft.name.trim()) throw new Error('Featured leader name is required');
        payload.name = featuredDraft.name.trim();
        payload.title = featuredDraft.title.trim() || null;
        payload.photo_url = featuredDraft.photo_url.trim() || null;
      }
      const row = await api.pmEventSpeakers.create(payload);
      setSpeakers((s) => [...s, row]);
      setFeaturedDraft({ mode: 'user', user_id: '', name: '', title: '', photo_url: '' });
      setFeaturedPhotoUploadError('');
      clearLocalPhotoPreview(setLocalFeaturedPhotoPreview);
      addToast({ type: 'success', title: 'Featured leader added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add featured leader', message: err.message });
    }
  };

  const removeSpeaker = async (speakerId) => {
    try {
      await api.pmEventSpeakers.delete(speakerId);
      setSpeakers((s) => s.filter((x) => x.id !== speakerId));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete speaker', message: err.message });
    }
  };

  const setSpeakerMc = async (speakerId, isMc) => {
    if (!canManage) return;
    const finplan = resolveLandingTemplateId(form.landing_template_id) === 'finplan';
    const patch = { is_mc: isMc };
    if (finplan) patch.role = isMc ? 'speaker' : 'special_guest';
    setMcBusyId(speakerId);
    try {
      const row = await api.pmEventSpeakers.update(speakerId, patch);
      setSpeakers((list) => list.map((sp) => (sp.id === speakerId ? row : sp)));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not update MC', message: err.message });
    } finally {
      setMcBusyId(null);
    }
  };

  const reassignSpeakerRole = async (speakerId, role) => {
    try {
      const row = await api.pmEventSpeakers.update(speakerId, { role });
      setSpeakers((list) => list.map((sp) => (sp.id === speakerId ? row : sp)));
      addToast({ type: 'success', title: 'Speaker reassigned' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not reassign speaker', message: err.message });
    }
  };

  const addPartner = async () => {
    if (!partnerDraft.name.trim()) {
      addToast({ type: 'error', title: 'Partner name is required' });
      return;
    }
    try {
      const row = await api.pmEventPartners.create({
        event_id: id,
        agency_id: user.agency_id,
        name: partnerDraft.name.trim(),
        logo_url: partnerDraft.logo_url?.trim() || null,
        sort_order: partners.length,
      });
      setPartners((p) => [...p, row]);
      setPartnerDraft({ name: '', logo_url: '' });
      setPartnerLogoUploadError('');
      setLocalPartnerLogoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      addToast({ type: 'success', title: 'Partner added' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add partner', message: err.message });
    }
  };

  const removePartner = async (partnerId) => {
    try {
      await api.pmEventPartners.delete(partnerId);
      setPartners((p) => p.filter((x) => x.id !== partnerId));
      setSpeakers((list) =>
        list.map((sp) =>
          sp.partner_id === partnerId ? { ...sp, partner_id: null, partner: null } : sp,
        ),
      );
      setGuestDraft((d) => (d.partner_id === partnerId ? { ...d, partner_id: '' } : d));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete partner', message: err.message });
    }
  };

  const movePartner = async (partnerId, direction) => {
    const index = partners.findIndex((p) => p.id === partnerId);
    if (index < 0) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= partners.length) return;

    const next = [...partners];
    const a = next[index];
    const b = next[swapIndex];
    next[index] = b;
    next[swapIndex] = a;

    const withOrder = next.map((p, i) => ({ ...p, sort_order: i }));
    setPartners(withOrder);

    try {
      await Promise.all([
        api.pmEventPartners.update(a.id, { sort_order: swapIndex }),
        api.pmEventPartners.update(b.id, { sort_order: index }),
      ]);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not reorder partners', message: err.message });
      await loadEvent();
    }
  };

  const isFinplan = resolveLandingTemplateId(form.landing_template_id) === 'finplan';
  const hosts = speakers.filter((sp) => sp.role === 'speaker');
  const guestSpeakers = speakers.filter((sp) => sp.role === 'special_guest');
  const featuredLeaders = speakers.filter((sp) => sp.role === 'featured_leader');
  const unassignedSpeakers = speakers.filter((sp) => sp.role === 'guest');
  const hostRows = isFinplan ? speakers.filter((sp) => sp.is_mc) : hosts;
  const guestRows = isFinplan ? speakers.filter((sp) => !sp.is_mc) : guestSpeakers;

  const hostPreviewUser = hostDraft.user_id
    ? agencyUsers.find((u) => u.id === hostDraft.user_id) || null
    : null;
  const featuredPreviewUser = featuredDraft.user_id
    ? agencyUsers.find((u) => u.id === featuredDraft.user_id) || null
    : null;

  const renderSpeakerRow = (sp, { showPartner = false } = {}) => {
    const preview = speakerPreview(sp);
    return (
      <li key={sp.id} className="py-3 flex items-start justify-between gap-3 text-sm">
        <div className="flex items-start gap-3 min-w-0">
          {preview.photo_url ? (
            <img
              src={preview.photo_url}
              alt=""
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
              className="h-10 w-10 rounded-full object-cover border border-gray-200 shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-medium shrink-0">
              {initialsFromName(preview.name)}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">{preview.name}</div>
            {preview.title && <div className="text-xs text-gray-500 truncate">{preview.title}</div>}
            {showPartner && sp.partner?.name && (
              <div className="text-xs text-gray-500 mt-0.5">{sp.partner.name}</div>
            )}
            {sp.user_id && (
              <div className="text-xs text-gray-400 mt-0.5">Linked profile</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {sp.is_mc && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
              MC
            </span>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={Boolean(sp.is_mc)}
              disabled={!canManage || mcBusyId === sp.id}
              onChange={(e) => { void setSpeakerMc(sp.id, e.target.checked); }}
            />
            MC
          </label>
          {canManage && (
            <button type="button" onClick={() => removeSpeaker(sp.id)} className="text-gray-400 hover:text-red-600">
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          )}
        </div>
      </li>
    );
  };

  const renderUserPreview = (u) => {
    if (!u) return null;
    return (
      <div className="flex items-center gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
        {u.headshot_url ? (
          <img
            src={u.headshot_url}
            alt=""
            width={40}
            height={40}
            loading="lazy"
            decoding="async"
            className="h-10 w-10 rounded-full object-cover border border-gray-200"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-white text-gray-500 flex items-center justify-center text-xs font-medium border border-gray-200">
            {initialsFromName(u.full_name)}
          </div>
        )}
        <div className="min-w-0 text-sm">
          <div className="font-medium text-gray-900 truncate">{u.full_name || 'User'}</div>
          {u.title && <div className="text-xs text-gray-500 truncate">{u.title}</div>}
          <div className="text-xs text-gray-400">Live from profile on the landing page</div>
        </div>
      </div>
    );
  };

  const renderHeadshotField = ({
    photoUrl,
    onPhotoUrlChange,
    localPreview,
    uploading,
    uploadError,
    fileInputRef,
    onUpload,
    previewAlt,
  }) => (
    <div className="space-y-2">
      <span className="text-sm font-medium text-gray-700 block">Headshot</span>
      <input
        ref={fileInputRef}
        type="file"
        accept={HERO_ACCEPT}
        className="hidden"
        onChange={onUpload}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
          {uploading ? 'Uploading…' : 'Upload photo'}
        </Button>
        <span className="text-xs text-gray-500">JPEG, PNG, WebP, or GIF · max 5 MB</span>
      </div>
      {uploadError && (
        <p className="text-sm text-red-500" role="alert">{uploadError}</p>
      )}
      <Input
        label="Or paste photo URL"
        value={photoUrl}
        onChange={onPhotoUrlChange}
        placeholder="https://…"
      />
      {(localPreview || photoUrl) && (
        <img
          src={localPreview || photoUrl}
          alt={previewAlt}
          width={56}
          height={56}
          loading="lazy"
          decoding="async"
          className="h-14 w-14 rounded-full object-cover border border-gray-200"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!canManage && isNew) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">You need a licensed role (or admin) to create events.</p>
        <Button variant="outline" onClick={() => navigate('/mentors')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/mentors')} className="text-gray-500 hover:text-gray-800">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{isNew ? 'New Academy event' : form.title || 'Edit Academy event'}</h1>
            {!isNew && form.status === 'published' && (
              <div className="space-y-0.5">
                <a
                  href={getPmEventLiveUrl(form.slug)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary hover:underline block"
                >
                  {getPmEventLiveUrl(form.slug)}
                </a>
                {getPmCustomDomainUrl(form.custom_domain) && (
                  <a
                    href={getPmCustomDomainUrl(form.custom_domain)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline block"
                  >
                    {getPmCustomDomainUrl(form.custom_domain)}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Link to={`/mentors/${id}/registrations`}>
              <Button variant="outline" size="sm">
                <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                Registrations
              </Button>
            </Link>
          )}
          {canManage && (
            <Button onClick={handleSave} isLoading={saving}>
              {isNew ? 'Create' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="flex items-center border-b border-gray-200">
          <div className="flex flex-1 overflow-x-auto" role="tablist" aria-label="Event editor sections">
            {EDITOR_TABS.map(({ id: tab, label }) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 bg-gray-50/50">
          {activeTab === 'details' && (
            <div className="space-y-4" role="tabpanel">
              <Input
                label="Title"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                disabled={!canManage}
              />
              <label className="block text-sm">
                <span className="font-medium text-gray-700 mb-1 block">Short description</span>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[88px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                  value={form.short_description || ''}
                  onChange={(e) => setField('short_description', e.target.value.slice(0, 280))}
                  disabled={!canManage}
                  maxLength={280}
                  placeholder="2–3 sentences for the public “what to expect” section"
                />
                <span className="text-xs text-gray-500 mt-1 block text-right">
                  {(form.short_description || '').length}/280
                </span>
              </label>
              <Input
                label="Slug"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField('slug', slugify(e.target.value));
                }}
                disabled={!canManage}
              />
              <div>
                <Input
                  label="Custom Domain"
                  value={form.custom_domain || ''}
                  onChange={(e) => setField('custom_domain', e.target.value)}
                  onBlur={() => {
                    const normalized = normalizeCustomDomain(form.custom_domain);
                    if (normalized) setField('custom_domain', normalized);
                    else if (!String(form.custom_domain || '').trim()) setField('custom_domain', '');
                  }}
                  placeholder="fintechexpansion.com"
                  disabled={!canManage}
                />
                <p className="text-xs text-gray-500 mt-1">
                  After saving, add this domain as a Domain alias in Netlify and point its DNS there — this
                  isn&apos;t automatic.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 mb-1 block">Language</span>
                  <select
                    className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                    value={form.language}
                    onChange={(e) => setField('language', e.target.value)}
                    disabled={!canManage}
                  >
                    {PM_LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 mb-1 block">Format</span>
                  <select
                    className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                    value={form.format}
                    onChange={(e) => setField('format', e.target.value)}
                    disabled={!canManage}
                  >
                    {PM_FORMAT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 mb-1 block">Status</span>
                  <select
                    className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value)}
                    disabled={!canManage}
                  >
                    {PM_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_main_event)}
                  onChange={(e) => setField('is_main_event', e.target.checked)}
                  disabled={!canManage}
                />
                Main event for this agency
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.allow_self_serve_guests)}
                  onChange={(e) => setField('allow_self_serve_guests', e.target.checked)}
                  disabled={!canManage}
                />
                Allow +1 guest on public registration
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.pipeline_event_type === 'business_launch'}
                  onChange={(e) => {
                    setField('pipeline_event_type', e.target.checked ? 'business_launch' : null);
                    if (!e.target.checked) setAssigneeManual(false);
                  }}
                  disabled={!canManage}
                />
                Business Launch
              </label>
              <div className="rounded-md border border-gray-200 bg-white p-4 space-y-2">
                <UserPickerDropdown
                  label="Marketing Director (fallback owner)"
                  source="directory"
                  users={mdAllowlist}
                  value={form.marketing_director_id}
                  onChange={(id) => setField('marketing_director_id', id)}
                  placeholder="Search Marketing Directors…"
                  disabled={!canManage}
                />
                <p className="text-xs text-gray-500">
                  Used when a registration has no invite-link owner and no CRM agent — for example a Business Launch with only a free-text assignee.
                </p>
              </div>
              {form.pipeline_event_type === 'business_launch' && (
                <div className="space-y-3 rounded-md border border-gray-200 bg-white p-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Assign this launch</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Link a CRM user, or enter contact details if they do not have an account yet.
                    </p>
                  </div>
                  <UserPickerDropdown
                    label="CRM user"
                    source="directory"
                    value={form.agent_id}
                    onChange={(id) => {
                      setField('agent_id', id);
                      if (id) setAssigneeManual(false);
                    }}
                    placeholder="Search agency users…"
                    disabled={!canManage}
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={assigneeManual}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setAssigneeManual(next);
                        if (next) setField('agent_id', null);
                      }}
                      disabled={!canManage}
                    />
                    Person not in the system yet
                  </label>
                  {assigneeManual && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        label="Name"
                        value={form.business_launch_assignee_name || ''}
                        onChange={(e) => setField('business_launch_assignee_name', e.target.value)}
                        disabled={!canManage}
                      />
                      <Input
                        label="Phone"
                        value={form.business_launch_assignee_phone || ''}
                        onChange={(e) => setField('business_launch_assignee_phone', e.target.value)}
                        disabled={!canManage}
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={form.business_launch_assignee_email || ''}
                        onChange={(e) => setField('business_launch_assignee_email', e.target.value)}
                        disabled={!canManage}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900">Landing template</h3>
                <p className="text-xs text-gray-500">
                  Chooses the layout used on the public event page. Default is Classic.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Landing template">
                  {PM_LANDING_TEMPLATES.map((t) => {
                    const selected = resolveLandingTemplateId(form.landing_template_id) === t.value;
                    return (
                      <label
                        key={t.value}
                        className={`block rounded-md border p-3 text-sm transition-colors ${
                          canManage ? 'cursor-pointer' : 'cursor-default opacity-80'
                        } ${
                          selected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name="landing_template_id"
                          value={t.value}
                          checked={selected}
                          onChange={() => setField('landing_template_id', t.value)}
                          disabled={!canManage}
                        />
                        <span className="font-medium text-gray-900 block">{t.label}</span>
                        <span className="text-xs text-gray-500 mt-1 block">{t.description}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {isFinplan && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Input
                      label="Headline"
                      value={form.landing_headline || ''}
                      maxLength={120}
                      onChange={(e) => setField('landing_headline', e.target.value)}
                      disabled={!canManage}
                    />
                    <p className="mt-1 text-xs text-gray-500">Optional line above the title. 120 characters max.</p>
                  </div>
                  <div>
                    <Input
                      label="Badge text"
                      value={form.landing_badge || ''}
                      maxLength={30}
                      onChange={(e) => setField('landing_badge', e.target.value)}
                      disabled={!canManage}
                    />
                    <p className="mt-1 text-xs text-gray-500">script tag next to the title, e.g. English</p>
                  </div>
                </div>
              )}

              <fieldset className="space-y-2" disabled={!canManage}>
                <legend className="text-sm font-medium text-gray-700">Registration covers</legend>
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="registration_scope"
                    className="mt-0.5"
                    checked={(form.registration_scope || 'single_session') !== 'all_sessions'}
                    onChange={() => setField('registration_scope', 'single_session')}
                  />
                  <span>One session (attendee picks)</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="registration_scope"
                    className="mt-0.5"
                    checked={form.registration_scope === 'all_sessions'}
                    onChange={() => setField('registration_scope', 'all_sessions')}
                  />
                  <span>All sessions (one sign-up covers every session)</span>
                </label>
              </fieldset>

              <div className="space-y-3 pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900">Hero image</h3>
                <p className="text-xs text-gray-500">
                  Used on the event landing page and for Open Graph / Twitter previews.
                </p>
                <Input
                  label="Hero image URL"
                  value={form.hero_image_url || ''}
                  onChange={(e) => {
                    setHeroUploadError('');
                    setField('hero_image_url', e.target.value);
                  }}
                  disabled={!canManage || heroUploading}
                  placeholder="https://…"
                />
                {canManage && (
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      ref={heroFileInputRef}
                      type="file"
                      accept={HERO_ACCEPT}
                      className="sr-only"
                      onChange={handleHeroUpload}
                      disabled={saving || heroUploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving || heroUploading || !user?.id}
                      isLoading={heroUploading}
                      onClick={() => heroFileInputRef.current?.click()}
                    >
                      <SafeIcon icon={FiUpload} className="w-3.5 h-3.5 mr-1.5" />
                      {heroUploading ? 'Uploading…' : 'Upload Image'}
                    </Button>
                    <p className="text-xs text-gray-500">
                      JPEG, PNG, WebP, or GIF · max 5 MB. Upload fills the URL field above.
                    </p>
                  </div>
                )}
                {heroUploadError && (
                  <p className="text-sm text-red-500" role="alert">{heroUploadError}</p>
                )}
                {(localHeroPreview || form.hero_image_url) && (
                  <div className="flex items-start gap-4">
                    <img
                      src={localHeroPreview || form.hero_image_url}
                      alt="Hero preview"
                      className="h-28 w-full max-w-xs rounded-md object-cover border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-4" role="tabpanel">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(form.requires_payment)}
                  onChange={(e) => setPaymentField('requires_payment', e.target.checked)}
                  disabled={!canManage}
                />
                Requires payment
              </label>
              <p className="text-xs text-gray-500">
                Registrants are sent to Stripe Checkout after they register. Leave off for free events.
              </p>

              {form.requires_payment && (
                <div className="space-y-4 rounded-md border border-gray-200 bg-white p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Input
                        label="Early bird price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.early_bird_price ?? ''}
                        onChange={(e) => setPaymentField('early_bird_price', e.target.value)}
                        disabled={!canManage}
                        placeholder="19.99"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to skip early-bird pricing</p>
                      {paymentErrors.early_bird_price && (
                        <p className="text-sm text-red-500 mt-1" role="alert">{paymentErrors.early_bird_price}</p>
                      )}
                    </div>
                    <div>
                      <Input
                        label="Early bird ends"
                        type="datetime-local"
                        value={form.early_bird_deadline || ''}
                        onChange={(e) => setPaymentField('early_bird_deadline', e.target.value)}
                        disabled={!canManage || !hasEarlyBird}
                      />
                      {paymentErrors.early_bird_deadline && (
                        <p className="text-sm text-red-500 mt-1" role="alert">{paymentErrors.early_bird_deadline}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Input
                      label={hasEarlyBird ? 'Price after early bird' : 'Price'}
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.late_price ?? ''}
                      onChange={(e) => setPaymentField('late_price', e.target.value)}
                      disabled={!canManage}
                      placeholder="24.99"
                    />
                    {paymentErrors.late_price && (
                      <p className="text-sm text-red-500 mt-1" role="alert">{paymentErrors.late_price}</p>
                    )}
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    {formatPmPrice(currentPrice.amount) ? (
                      <>
                        <p className="text-sm font-medium text-gray-900">
                          Registrants pay {formatPmPrice(currentPrice.amount)} right now
                        </p>
                        {currentPrice.tier === 'early_bird' && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Early-bird pricing is active until {new Date(fromLocalInputValue(form.early_bird_deadline)).toLocaleString()}.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Set a price to see what registrants pay.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="space-y-4" role="tabpanel">
              {isNew ? (
                <p className="text-sm text-gray-500">Create the event first to add sessions.</p>
              ) : (
                <>
                  <ul className="divide-y divide-gray-100">
                    {sessions.map((s) => (
                      <li key={s.id} className="py-2 text-sm">
                        {editingSessionId === s.id ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input label="Session title" value={sessionEditDraft.title} onChange={(e) => setSessionEditDraft((d) => ({ ...d, title: e.target.value }))} />
                            <Input label="Capacity" type="number" value={sessionEditDraft.capacity} onChange={(e) => setSessionEditDraft((d) => ({ ...d, capacity: e.target.value }))} />
                            <Input label="Starts" type="datetime-local" value={sessionEditDraft.starts_at} onChange={(e) => setSessionEditDraft((d) => ({ ...d, starts_at: e.target.value }))} />
                            <Input label="Ends" type="datetime-local" value={sessionEditDraft.ends_at} onChange={(e) => setSessionEditDraft((d) => ({ ...d, ends_at: e.target.value }))} />
                            <SessionTimezoneField value={sessionEditDraft.timezone} onChange={(tz) => setSessionEditDraft((d) => ({ ...d, timezone: tz }))} />
                            <Input label="Location" value={sessionEditDraft.location_text} onChange={(e) => setSessionEditDraft((d) => ({ ...d, location_text: e.target.value }))} />
                            <Input label="Meeting URL" value={sessionEditDraft.meeting_url} onChange={(e) => setSessionEditDraft((d) => ({ ...d, meeting_url: e.target.value }))} />
                            {form.requires_payment && (
                              <div className="sm:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300"
                                    checked={Boolean(sessionEditDraft.requires_payment)}
                                    onChange={(e) => setSessionEditDraft((d) => ({ ...d, requires_payment: e.target.checked }))}
                                  />
                                  Requires payment
                                </label>
                              </div>
                            )}
                            <div className="sm:col-span-2 flex flex-wrap gap-2">
                              <Button type="button" size="sm" onClick={saveSessionEdit}>
                                Save session
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={cancelEditSession}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{s.title || 'Session'}</span>
                                {s.requires_payment && (
                                  <span className="px-1.5 py-0.5 text-[11px] font-medium rounded bg-amber-100 text-amber-800">
                                    Paid
                                  </span>
                                )}
                              </div>
                              <SessionTimes startsAt={s.starts_at} endsAt={s.ends_at} timezone={s.timezone} className="my-0.5" />
                              {s.location_text && <div className="text-gray-500">{s.location_text}</div>}
                              {s.meeting_url && (
                                <div className="text-gray-500 truncate max-w-md">{s.meeting_url}</div>
                              )}
                            </div>
                            {canManage && (
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => startEditSession(s)}
                                  className="text-gray-400 hover:text-gray-700"
                                  aria-label="Edit session"
                                >
                                  <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeSession(s.id)}
                                  className="text-gray-400 hover:text-red-600"
                                  aria-label="Delete session"
                                >
                                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                    {sessions.length === 0 && (
                      <li className="py-2 text-sm text-gray-500">No sessions yet.</li>
                    )}
                  </ul>
                  {canManage && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                      <Input label="Session title" value={sessionDraft.title} onChange={(e) => setSessionDraft((d) => ({ ...d, title: e.target.value }))} />
                      <Input label="Capacity" type="number" value={sessionDraft.capacity} onChange={(e) => setSessionDraft((d) => ({ ...d, capacity: e.target.value }))} />
                      <Input label="Starts" type="datetime-local" value={sessionDraft.starts_at} onChange={(e) => setSessionDraft((d) => ({ ...d, starts_at: e.target.value }))} />
                      <Input label="Ends" type="datetime-local" value={sessionDraft.ends_at} onChange={(e) => setSessionDraft((d) => ({ ...d, ends_at: e.target.value }))} />
                      <SessionTimezoneField value={sessionDraft.timezone} onChange={(tz) => setSessionDraft((d) => ({ ...d, timezone: tz }))} />
                      <Input label="Location" value={sessionDraft.location_text} onChange={(e) => setSessionDraft((d) => ({ ...d, location_text: e.target.value }))} />
                      <Input label="Meeting URL" value={sessionDraft.meeting_url} onChange={(e) => setSessionDraft((d) => ({ ...d, meeting_url: e.target.value }))} />
                      {form.requires_payment && (
                        <div className="sm:col-span-2">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={Boolean(sessionDraft.requires_payment)}
                              onChange={(e) => setSessionDraft((d) => ({ ...d, requires_payment: e.target.checked }))}
                            />
                            Requires payment
                          </label>
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <Button type="button" variant="outline" size="sm" onClick={addSession}>
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                          Add session
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'speakers' && (
            <div className="space-y-6" role="tabpanel">
              {isNew ? (
                <p className="text-sm text-gray-500">Create the event first to manage speakers.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-500">
                    {isFinplan
                      ? 'FinPlan Template groups: MC / Hosts (1–2 recommended) and Speakers (up to 8 recommended). Turning MC on saves the person as a host; turning it off saves them as a guest speaker, so another template still has a tier for them.'
                      : 'Organized to match Fintech Leaders landing tiers: Hosted by, Guest speakers, Featured leaders. The MC toggle is used by the FinPlan Template.'}
                  </p>

                  {!isFinplan && unassignedSpeakers.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                      <div>
                        <h3 className="text-sm font-medium text-amber-900">Unassigned</h3>
                        <p className="text-xs text-amber-800 mt-0.5">
                          Legacy “guest” rows are not shown on the landing. Reassign each to a landing tier.
                        </p>
                      </div>
                      <ul className="divide-y divide-amber-100">
                        {unassignedSpeakers.map((sp) => {
                          const preview = speakerPreview(sp);
                          return (
                            <li key={sp.id} className="py-2 flex flex-wrap items-center justify-between gap-3 text-sm">
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">{preview.name}</div>
                                {preview.title && <div className="text-xs text-gray-500">{preview.title}</div>}
                              </div>
                              <div className="flex items-center gap-2">
                                {sp.is_mc && (
                                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                    MC
                                  </span>
                                )}
                                <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300"
                                    checked={Boolean(sp.is_mc)}
                                    disabled={!canManage || mcBusyId === sp.id}
                                    onChange={(e) => { void setSpeakerMc(sp.id, e.target.checked); }}
                                  />
                                  MC
                                </label>
                                {canManage && (
                                  <select
                                    className="border border-gray-300 rounded-md h-9 px-2 text-sm bg-white"
                                    defaultValue=""
                                    onChange={(e) => {
                                      const role = e.target.value;
                                      if (!role) return;
                                      reassignSpeakerRole(sp.id, role);
                                    }}
                                  >
                                    <option value="">Reassign to…</option>
                                    {PM_SPEAKER_LANDING_ROLES.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                )}
                                {canManage && (
                                  <button type="button" onClick={() => removeSpeaker(sp.id)} className="text-gray-400 hover:text-red-600">
                                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-medium text-gray-900">{isFinplan ? 'MC / Hosts' : 'Hosted by'}</h3>
                      <span className="text-xs text-gray-400">
                        {hostRows.length} / {isFinplan ? FINPLAN_HOST_SOFT_LIMIT : HOST_SOFT_LIMIT} recommended
                      </span>
                    </div>
                    {isFinplan && hostRows.length > FINPLAN_HOST_SOFT_LIMIT && (
                      <p className="text-xs text-amber-700">FinPlan landing pages work best with 1–2 MC / hosts</p>
                    )}
                    {!isFinplan && hosts.length > HOST_SOFT_LIMIT && (
                      <p className="text-xs text-amber-700">Landing pages display up to 4 hosts</p>
                    )}
                    <ul className="divide-y divide-gray-100">
                      {hostRows.length === 0 && (
                        <li className="py-2 text-sm text-gray-400">{isFinplan ? 'No MC / hosts yet' : 'No hosts yet'}</li>
                      )}
                      {hostRows.map((sp) => renderSpeakerRow(sp))}
                    </ul>
                    {canManage && (
                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div className="flex gap-4 text-sm">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={hostDraft.mode === 'user'}
                              onChange={() => setHostDraft((d) => ({ ...d, mode: 'user', name: '', title: '', photo_url: '' }))}
                            />
                            Agency user
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={hostDraft.mode === 'manual'}
                              onChange={() => setHostDraft((d) => ({ ...d, mode: 'manual', user_id: '' }))}
                            />
                            Manual entry
                          </label>
                        </div>
                        {hostDraft.mode === 'user' ? (
                          <>
                            <UserPickerDropdown
                              label={isFinplan ? 'MC / host' : 'Host'}
                              source="directory"
                              value={hostDraft.user_id || null}
                              onChange={(uid) => setHostDraft((d) => ({ ...d, user_id: uid || '' }))}
                              placeholder="Search agency users…"
                            />
                            {renderUserPreview(hostPreviewUser)}
                          </>
                        ) : (
                          <>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <Input
                                label="Name"
                                value={hostDraft.name}
                                onChange={(e) => setHostDraft((d) => ({ ...d, name: e.target.value }))}
                              />
                              <Input
                                label="Title"
                                value={hostDraft.title}
                                onChange={(e) => setHostDraft((d) => ({ ...d, title: e.target.value }))}
                              />
                            </div>
                            {renderHeadshotField({
                              photoUrl: hostDraft.photo_url,
                              onPhotoUrlChange: (e) => setHostDraft((d) => ({ ...d, photo_url: e.target.value })),
                              localPreview: localHostPhotoPreview,
                              uploading: hostPhotoUploading,
                              uploadError: hostPhotoUploadError,
                              fileInputRef: hostPhotoFileInputRef,
                              onUpload: handleHostPhotoUpload,
                              previewAlt: 'Host headshot preview',
                            })}
                          </>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={() => addHost({ isMc: isFinplan })}>
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                          {isFinplan ? 'Add MC / host' : 'Add host'}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 border-t border-gray-100 pt-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-medium text-gray-900">{isFinplan ? 'Speakers' : 'Guest speakers'}</h3>
                      <span className="text-xs text-gray-400">
                        {guestRows.length} / {isFinplan ? FINPLAN_SPEAKER_SOFT_LIMIT : GUEST_SOFT_LIMIT} recommended
                      </span>
                    </div>
                    {isFinplan && guestRows.length > FINPLAN_SPEAKER_SOFT_LIMIT && (
                      <p className="text-xs text-amber-700">FinPlan landing pages display up to 8 speakers</p>
                    )}
                    {!isFinplan && guestSpeakers.length > GUEST_SOFT_LIMIT && (
                      <p className="text-xs text-amber-700">Landing pages display up to 4 guest speakers</p>
                    )}
                    <ul className="divide-y divide-gray-100">
                      {guestRows.length === 0 && (
                        <li className="py-2 text-sm text-gray-400">{isFinplan ? 'No speakers yet' : 'No guest speakers yet'}</li>
                      )}
                      {guestRows.map((sp) => renderSpeakerRow(sp, { showPartner: true }))}
                    </ul>
                    {canManage && (
                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input
                            label="Name"
                            value={guestDraft.name}
                            onChange={(e) => setGuestDraft((d) => ({ ...d, name: e.target.value }))}
                          />
                          <Input
                            label="Title"
                            value={guestDraft.title}
                            onChange={(e) => setGuestDraft((d) => ({ ...d, title: e.target.value }))}
                          />
                        </div>
                        <label className="block text-sm">
                          <span className="font-medium text-gray-700 mb-1 flex items-center justify-between gap-2">
                            <span>Short bio</span>
                            <span className="text-xs font-normal text-gray-400">
                              {guestDraft.bio.length}/{GUEST_BIO_MAX}
                            </span>
                          </span>
                          <textarea
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[72px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            value={guestDraft.bio}
                            maxLength={GUEST_BIO_MAX}
                            onChange={(e) => setGuestDraft((d) => ({ ...d, bio: e.target.value.slice(0, GUEST_BIO_MAX) }))}
                            placeholder="Very short bio for the landing page"
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="font-medium text-gray-700 mb-1 block">Partner affiliation (optional)</span>
                          <select
                            className="w-full border border-gray-300 rounded-md h-10 px-3 text-sm"
                            value={guestDraft.partner_id}
                            onChange={(e) => setGuestDraft((d) => ({ ...d, partner_id: e.target.value }))}
                          >
                            <option value="">None</option>
                            {partners.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          {partners.length === 0 && (
                            <span className="text-xs text-gray-500 mt-1 block">Add partners on the Partners tab to enable affiliation.</span>
                          )}
                        </label>
                        {renderHeadshotField({
                          photoUrl: guestDraft.photo_url,
                          onPhotoUrlChange: (e) => setGuestDraft((d) => ({ ...d, photo_url: e.target.value })),
                          localPreview: localSpeakerPhotoPreview,
                          uploading: speakerPhotoUploading,
                          uploadError: speakerPhotoUploadError,
                          fileInputRef: speakerPhotoFileInputRef,
                          onUpload: handleSpeakerPhotoUpload,
                          previewAlt: 'Guest headshot preview',
                        })}
                        <Button type="button" variant="outline" size="sm" onClick={addGuestSpeaker}>
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                          {isFinplan ? 'Add speaker' : 'Add guest speaker'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {!isFinplan && (
                  <div className="space-y-3 border-t border-gray-100 pt-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-medium text-gray-900">Featured leaders</h3>
                      <span className="text-xs text-gray-400">{featuredLeaders.length} / {FEATURED_SOFT_LIMIT} recommended</span>
                    </div>
                    {featuredLeaders.length > FEATURED_SOFT_LIMIT && (
                      <p className="text-xs text-amber-700">Landing pages display up to 6 featured leaders</p>
                    )}
                    <ul className="divide-y divide-gray-100">
                      {featuredLeaders.length === 0 && (
                        <li className="py-2 text-sm text-gray-400">No featured leaders yet</li>
                      )}
                      {featuredLeaders.map((sp) => renderSpeakerRow(sp))}
                    </ul>
                    {canManage && (
                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div className="flex gap-4 text-sm">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={featuredDraft.mode === 'user'}
                              onChange={() => setFeaturedDraft((d) => ({ ...d, mode: 'user', name: '', title: '', photo_url: '' }))}
                            />
                            Agency user
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={featuredDraft.mode === 'manual'}
                              onChange={() => setFeaturedDraft((d) => ({ ...d, mode: 'manual', user_id: '' }))}
                            />
                            Manual entry
                          </label>
                        </div>
                        {featuredDraft.mode === 'user' ? (
                          <>
                            <UserPickerDropdown
                              label="Featured leader"
                              source="directory"
                              value={featuredDraft.user_id || null}
                              onChange={(uid) => setFeaturedDraft((d) => ({ ...d, user_id: uid || '' }))}
                              placeholder="Search agency users…"
                            />
                            {renderUserPreview(featuredPreviewUser)}
                          </>
                        ) : (
                          <>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <Input
                                label="Name"
                                value={featuredDraft.name}
                                onChange={(e) => setFeaturedDraft((d) => ({ ...d, name: e.target.value }))}
                              />
                              <Input
                                label="Title"
                                value={featuredDraft.title}
                                onChange={(e) => setFeaturedDraft((d) => ({ ...d, title: e.target.value }))}
                              />
                            </div>
                            {renderHeadshotField({
                              photoUrl: featuredDraft.photo_url,
                              onPhotoUrlChange: (e) => setFeaturedDraft((d) => ({ ...d, photo_url: e.target.value })),
                              localPreview: localFeaturedPhotoPreview,
                              uploading: featuredPhotoUploading,
                              uploadError: featuredPhotoUploadError,
                              fileInputRef: featuredPhotoFileInputRef,
                              onUpload: handleFeaturedPhotoUpload,
                              previewAlt: 'Featured leader headshot preview',
                            })}
                          </>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={addFeaturedLeader}>
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                          Add featured leader
                        </Button>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'sections' && (
            <div className="space-y-5" role="tabpanel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm text-gray-500 max-w-xl">
                  {isFinplan
                    ? 'The FinPlan Template prints enabled sections in this order. Leave the heading blank to use the default.'
                    : 'Saved with the event. The FinPlan Template prints enabled sections; other templates ignore them for now.'}
                </p>
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canManage || PM_LANDING_SECTION_TYPES.every((type) => landingSections.some((section) => section.type === type.key))}
                    onClick={() => setSectionMenuOpen((open) => !open)}
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                    Add section
                  </Button>
                  {sectionMenuOpen && (
                    <div className="absolute right-0 z-10 mt-1 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                      {PM_LANDING_SECTION_TYPES.map((type) => {
                        const used = landingSections.some((section) => section.type === type.key);
                        return (
                          <button
                            key={type.key}
                            type="button"
                            className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                            disabled={!canManage || used}
                            onClick={() => addLandingSection(type.key)}
                          >
                            {type.label}{used ? ' (added)' : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {landingSections.length === 0 && (
                <p className="text-sm text-gray-400">No sections yet.</p>
              )}

              {landingSections.map((section, sectionIndex) => {
                const type = landingSectionType(section.type);
                const items = Array.isArray(section.items) ? section.items : [];
                return (
                  <div key={section.id || sectionIndex} className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900">{type?.label || section.type}</h3>
                        {type && (
                          <span className="text-xs text-gray-400">{items.length} / {type.maxItems}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={section.enabled !== false}
                            disabled={!canManage}
                            onChange={(e) => patchLandingSection(section.id, (current) => ({ ...current, enabled: e.target.checked }))}
                          />
                          Enabled
                        </label>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                          disabled={!canManage || sectionIndex === 0}
                          onClick={() => setForm((current) => ({
                            ...current,
                            landing_sections: moveListItem(current.landing_sections || [], sectionIndex, -1),
                          }))}
                          aria-label="Move section up"
                        >
                          <SafeIcon icon={FiChevronUp} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                          disabled={!canManage || sectionIndex === landingSections.length - 1}
                          onClick={() => setForm((current) => ({
                            ...current,
                            landing_sections: moveListItem(current.landing_sections || [], sectionIndex, 1),
                          }))}
                          aria-label="Move section down"
                        >
                          <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                        </button>
                        {canManage && (
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-600"
                            onClick={() => setForm((current) => ({
                              ...current,
                              landing_sections: (current.landing_sections || []).filter((row) => row.id !== section.id),
                            }))}
                            aria-label="Delete section"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {!type ? (
                      <p className="text-sm text-gray-500">This section type is kept as saved. Add it to the section registry to edit it here.</p>
                    ) : (
                      <>
                        <div>
                          <Input
                            label="Heading"
                            value={section.heading || ''}
                            maxLength={80}
                            placeholder={type.defaultHeading}
                            onChange={(e) => patchLandingSection(section.id, (current) => ({ ...current, heading: e.target.value }))}
                            disabled={!canManage}
                          />
                          <p className="mt-1 text-xs text-gray-500">Optional. Blank uses “{type.defaultHeading}”.</p>
                        </div>
                        {type.disclaimer && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{type.disclaimer.label}</label>
                            <textarea
                              value={section.disclaimer || ''}
                              maxLength={type.disclaimer.max}
                              rows={3}
                              disabled={!canManage}
                              onChange={(e) => patchLandingSection(section.id, (current) => ({ ...current, disclaimer: e.target.value }))}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            />
                            <p className="mt-1 text-xs text-gray-500">Printed under the section. {type.disclaimer.max} characters max.</p>
                          </div>
                        )}
                        <div className="space-y-3">
                          {items.map((item, itemIndex) => (
                            <div key={`${section.id}-${itemIndex}`} className="rounded-md border border-gray-100 bg-gray-50/60 p-3 space-y-3">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-500">Item {itemIndex + 1}</span>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                    disabled={!canManage || itemIndex === 0}
                                    onClick={() => patchLandingSection(section.id, (current) => ({
                                      ...current,
                                      items: moveListItem(current.items || [], itemIndex, -1),
                                    }))}
                                    aria-label="Move item up"
                                  >
                                    <SafeIcon icon={FiChevronUp} className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                    disabled={!canManage || itemIndex === items.length - 1}
                                    onClick={() => patchLandingSection(section.id, (current) => ({
                                      ...current,
                                      items: moveListItem(current.items || [], itemIndex, 1),
                                    }))}
                                    aria-label="Move item down"
                                  >
                                    <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                                  </button>
                                  {canManage && (
                                    <button
                                      type="button"
                                      className="text-gray-400 hover:text-red-600"
                                      onClick={() => patchLandingSection(section.id, (current) => ({
                                        ...current,
                                        items: (current.items || []).filter((_, index) => index !== itemIndex),
                                      }))}
                                      aria-label="Delete item"
                                    >
                                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {type.fields.map((field) => {
                                const value = item?.[field.key] || '';
                                if (field.input === 'photo') {
                                  const busy = sectionPhotoKey === `${section.id}:${itemIndex}`;
                                  return (
                                    <div key={field.key} className="space-y-2">
                                      <span className="block text-sm font-medium text-gray-700">{field.label}</span>
                                      {value && (
                                        <img src={value} alt="" className="h-14 w-14 rounded-full object-cover border border-gray-200" />
                                      )}
                                      <div className="flex flex-wrap items-center gap-2">
                                        <input
                                          type="file"
                                          accept={HERO_ACCEPT}
                                          className="sr-only"
                                          id={`section-photo-${section.id}-${itemIndex}`}
                                          disabled={!canManage || busy}
                                          onChange={(e) => { void uploadSectionItemPhoto(section.id, itemIndex, e); }}
                                        />
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          disabled={!canManage || busy}
                                          onClick={() => document.getElementById(`section-photo-${section.id}-${itemIndex}`)?.click()}
                                        >
                                          {busy ? 'Uploading…' : 'Upload photo'}
                                        </Button>
                                        {value && canManage && (
                                          <button
                                            type="button"
                                            className="text-xs text-gray-500 hover:text-red-600"
                                            onClick={() => patchLandingSection(section.id, (current) => {
                                              const nextItems = [...(current.items || [])];
                                              nextItems[itemIndex] = { ...nextItems[itemIndex], photo_url: '' };
                                              return { ...current, items: nextItems };
                                            })}
                                          >
                                            Remove photo
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                if (field.input === 'textarea') {
                                  return (
                                    <div key={field.key}>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {field.label}{field.required ? '' : ' (optional)'}
                                      </label>
                                      <textarea
                                        value={value}
                                        maxLength={field.max}
                                        rows={3}
                                        disabled={!canManage}
                                        onChange={(e) => patchLandingSection(section.id, (current) => {
                                          const nextItems = [...(current.items || [])];
                                          nextItems[itemIndex] = { ...nextItems[itemIndex], [field.key]: e.target.value };
                                          return { ...current, items: nextItems };
                                        })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                                      />
                                    </div>
                                  );
                                }
                                return (
                                  <Input
                                    key={field.key}
                                    label={field.required ? field.label : `${field.label} (optional)`}
                                    value={value}
                                    maxLength={field.max}
                                    disabled={!canManage}
                                    onChange={(e) => patchLandingSection(section.id, (current) => {
                                      const nextItems = [...(current.items || [])];
                                      nextItems[itemIndex] = { ...nextItems[itemIndex], [field.key]: e.target.value };
                                      return { ...current, items: nextItems };
                                    })}
                                  />
                                );
                              })}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!canManage || items.length >= type.maxItems}
                            onClick={() => patchLandingSection(section.id, (current) => ({
                              ...current,
                              items: [...(current.items || []), newLandingItem(type.key)],
                            }))}
                          >
                            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                            Add item
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'partners' && (
            <div className="space-y-4" role="tabpanel">
              {isNew ? (
                <p className="text-sm text-gray-500">Create the event first to manage partners.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500">
                    Partner organizations shown on Fintech Leaders and similar templates. Guests can be linked to a partner on the Speakers tab.
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {partners.map((p, index) => (
                      <li key={p.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          {p.logo_url ? (
                            <img
                              src={p.logo_url}
                              alt=""
                              width={32}
                              height={32}
                              loading="lazy"
                              decoding="async"
                              className="h-8 w-8 rounded object-contain border border-gray-200 bg-white shrink-0"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded border border-dashed border-gray-200 shrink-0" />
                          )}
                          <div className="font-medium text-gray-900 truncate">{p.name}</div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => movePartner(p.id, 'up')}
                              disabled={index === 0}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              aria-label="Move partner up"
                            >
                              <SafeIcon icon={FiChevronUp} className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => movePartner(p.id, 'down')}
                              disabled={index === partners.length - 1}
                              className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              aria-label="Move partner down"
                            >
                              <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => removePartner(p.id)} className="text-gray-400 hover:text-red-600">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                    {partners.length === 0 && (
                      <li className="py-2 text-sm text-gray-500">No partners yet.</li>
                    )}
                  </ul>
                  {canManage && (
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      <Input
                        label="Partner name"
                        value={partnerDraft.name}
                        onChange={(e) => setPartnerDraft((d) => ({ ...d, name: e.target.value }))}
                      />
                      <Input
                        label="Logo URL"
                        value={partnerDraft.logo_url}
                        onChange={(e) => {
                          setPartnerLogoUploadError('');
                          setPartnerDraft((d) => ({ ...d, logo_url: e.target.value }));
                        }}
                        disabled={partnerLogoUploading}
                        placeholder="https://…"
                      />
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          ref={partnerLogoFileInputRef}
                          type="file"
                          accept={HERO_ACCEPT}
                          className="sr-only"
                          onChange={handlePartnerLogoUpload}
                          disabled={partnerLogoUploading}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={partnerLogoUploading || !user?.id}
                          isLoading={partnerLogoUploading}
                          onClick={() => partnerLogoFileInputRef.current?.click()}
                        >
                          <SafeIcon icon={FiUpload} className="w-3.5 h-3.5 mr-1.5" />
                          {partnerLogoUploading ? 'Uploading…' : 'Upload logo'}
                        </Button>
                        <p className="text-xs text-gray-500">JPEG, PNG, WebP, or GIF · max 5 MB</p>
                      </div>
                      {partnerLogoUploadError && (
                        <p className="text-sm text-red-500" role="alert">{partnerLogoUploadError}</p>
                      )}
                      {(localPartnerLogoPreview || partnerDraft.logo_url) && (
                        <img
                          src={localPartnerLogoPreview || partnerDraft.logo_url}
                          alt="Partner logo preview"
                          width={48}
                          height={48}
                          loading="lazy"
                          decoding="async"
                          className="h-12 w-12 rounded object-contain border border-gray-200 bg-white"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={addPartner}>
                        <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                        Add partner
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="space-y-8" role="tabpanel">
              {isNew ? (
                <p className="text-sm text-gray-500">Create the event first to manage email settings.</p>
              ) : emailSettingsLoading && !emailSettingsLoaded ? (
                <div className="py-8 flex justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  <section className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-medium text-gray-900">Confirmation email</h3>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={Boolean(emailForm.confirmation_enabled)}
                          onChange={(e) => setEmailField('confirmation_enabled', e.target.checked)}
                          disabled={!canManage}
                        />
                        Enabled
                      </label>
                    </div>
                    <div>
                      <Input
                        label="Subject"
                        value={emailForm.confirmation_subject}
                        onChange={(e) => setEmailField('confirmation_subject', e.target.value)}
                        disabled={!canManage || !emailForm.confirmation_enabled}
                        error={emailErrors.confirmation_subject}
                      />
                    </div>
                    <div>
                      <EmailBodyEditor
                        mergeKey="conf"
                        note={EMAIL_MERGE_FIELDS_NOTE}
                        value={emailForm.confirmation_body}
                        onChange={(next) => setEmailField('confirmation_body', next)}
                        disabled={!canManage || !emailForm.confirmation_enabled}
                        error={emailErrors.confirmation_body}
                        textareaRef={confirmationBodyRef}
                        placeholder="Hi {{first_name}}, thanks for registering for {{event_title}}…"
                        previewing={confirmationPreviewing}
                        onTogglePreview={() => setConfirmationPreviewing((v) => !v)}
                        showInviterCard={confirmationShowInviter}
                        onShowInviterCardChange={setConfirmationShowInviter}
                        previewSubject={confirmationPreviewSubject}
                        previewHtml={confirmationPreviewHtml}
                        previewTitle="Confirmation email preview"
                      />
                    </div>
                  </section>

                  <section className="space-y-4 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-medium text-gray-900">Reminder emails</h3>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={Boolean(emailForm.reminder_enabled)}
                          onChange={(e) => setEmailField('reminder_enabled', e.target.checked)}
                          disabled={!canManage}
                        />
                        Enabled
                      </label>
                    </div>
                    <div>
                      <Input
                        label="Subject"
                        value={emailForm.reminder_subject}
                        onChange={(e) => setEmailField('reminder_subject', e.target.value)}
                        disabled={!canManage || !emailForm.reminder_enabled}
                        error={emailErrors.reminder_subject}
                      />
                    </div>
                    <div>
                      <EmailBodyEditor
                        mergeKey="rem"
                        note={EMAIL_MERGE_FIELDS_NOTE}
                        value={emailForm.reminder_body}
                        onChange={(next) => setEmailField('reminder_body', next)}
                        disabled={!canManage || !emailForm.reminder_enabled}
                        error={emailErrors.reminder_body}
                        textareaRef={reminderBodyRef}
                        placeholder="Reminder: {{event_title}} is coming up on {{session_date}} at {{session_time}}…"
                        previewing={reminderPreviewing}
                        onTogglePreview={() => setReminderPreviewing((v) => !v)}
                        showInviterCard={reminderShowInviter}
                        onShowInviterCardChange={setReminderShowInviter}
                        previewSubject={reminderPreviewSubject}
                        previewHtml={reminderPreviewHtml}
                        previewTitle="Reminder email preview"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <span className="font-medium text-gray-700 text-sm">Reminder offsets</span>
                        {canManage && emailForm.reminder_enabled && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEmailField('reminder_offsets', [
                                ...(emailForm.reminder_offsets || []),
                                { amount: 24, unit: 'hours' },
                              ]);
                              setEmailErrors((errs) => {
                                if (!errs.reminder_offsets) return errs;
                                const next = { ...errs };
                                delete next.reminder_offsets;
                                return next;
                              });
                            }}
                          >
                            <SafeIcon icon={FiPlus} className="w-3.5 h-3.5 mr-1.5" />
                            Add offset
                          </Button>
                        )}
                      </div>
                      <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
                        {(emailForm.reminder_offsets || []).map((row, index) => (
                          <li key={`offset-${index}`} className="flex flex-wrap items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className="w-24 border border-gray-300 rounded-md h-10 px-3 text-sm disabled:opacity-50"
                              value={row.amount}
                              onChange={(e) => {
                                const next = [...(emailForm.reminder_offsets || [])];
                                next[index] = { ...next[index], amount: e.target.value === '' ? '' : Number(e.target.value) };
                                setEmailField('reminder_offsets', next);
                              }}
                              disabled={!canManage || !emailForm.reminder_enabled}
                            />
                            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
                              <button
                                type="button"
                                disabled={!canManage || !emailForm.reminder_enabled}
                                onClick={() => {
                                  const next = [...(emailForm.reminder_offsets || [])];
                                  next[index] = { ...next[index], unit: 'hours' };
                                  setEmailField('reminder_offsets', next);
                                }}
                                className={`px-3 h-10 ${row.unit === 'hours' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'} disabled:opacity-50`}
                              >
                                Hours
                              </button>
                              <button
                                type="button"
                                disabled={!canManage || !emailForm.reminder_enabled}
                                onClick={() => {
                                  const next = [...(emailForm.reminder_offsets || [])];
                                  next[index] = { ...next[index], unit: 'days' };
                                  setEmailField('reminder_offsets', next);
                                }}
                                className={`px-3 h-10 border-l border-gray-300 ${row.unit === 'days' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'} disabled:opacity-50`}
                              >
                                Days
                              </button>
                            </div>
                            <span className="text-xs text-gray-500">before session</span>
                            {canManage && emailForm.reminder_enabled && (
                              <button
                                type="button"
                                className="text-gray-400 hover:text-red-600 ml-auto"
                                aria-label="Remove offset"
                                onClick={() => {
                                  const next = (emailForm.reminder_offsets || []).filter((_, i) => i !== index);
                                  setEmailField('reminder_offsets', next);
                                }}
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            )}
                          </li>
                        ))}
                        {(emailForm.reminder_offsets || []).length === 0 && (
                          <li className="text-sm text-gray-500 py-1">No reminder offsets yet.</li>
                        )}
                      </ul>
                      {emailErrors.reminder_offsets && (
                        <p className="text-sm text-red-500 mt-1" role="alert">{emailErrors.reminder_offsets}</p>
                      )}
                    </div>
                  </section>

                  {form.requires_payment && (
                    <section className="space-y-4 pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-medium text-gray-900">Paid / VIP Confirmation</h3>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={Boolean(emailForm.paid_confirmation_enabled)}
                            onChange={(e) => setEmailField('paid_confirmation_enabled', e.target.checked)}
                            disabled={!canManage}
                          />
                          Enabled
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        Sent once payment succeeds, in addition to the confirmation email above.
                      </p>
                      <div>
                        <Input
                          label="Subject"
                          value={emailForm.paid_confirmation_subject}
                          onChange={(e) => setEmailField('paid_confirmation_subject', e.target.value)}
                          disabled={!canManage || !emailForm.paid_confirmation_enabled}
                          error={emailErrors.paid_confirmation_subject}
                        />
                      </div>
                      <div>
                        <EmailBodyEditor
                          mergeKey="paid"
                          mergeFields={PAID_EMAIL_MERGE_FIELDS}
                          note="{{paid_session_details}} is supported by this template only — it lists every session marked “Requires payment” with its date, time, and location. The other fields behave as they do in the confirmation and reminder emails, except {{session_time}} is a single start time in the session's timezone here. {{session_date}} / {{session_time}} / {{session_location}} refer to the first paid session."
                          value={emailForm.paid_confirmation_body}
                          onChange={(next) => setEmailField('paid_confirmation_body', next)}
                          disabled={!canManage || !emailForm.paid_confirmation_enabled}
                          error={emailErrors.paid_confirmation_body}
                          textareaRef={paidBodyRef}
                          placeholder="Payment confirmed, {{first_name}}! Your spot in {{event_title}} is reserved: {{paid_session_details}}"
                          previewing={paidPreviewing}
                          onTogglePreview={() => setPaidPreviewing((v) => !v)}
                          showInviterCard={paidShowInviter}
                          onShowInviterCardChange={setPaidShowInviter}
                          previewSubject={paidPreviewSubject}
                          previewHtml={paidPreviewHtml}
                          previewTitle="Paid confirmation email preview"
                        />
                      </div>
                    </section>
                  )}

                  {emailSettingsDirty && (
                    <p className="text-xs text-amber-700">Unsaved email changes — use Save in the header to write them.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
